import React, {useContext, useEffect, useState} from 'react';
import {
    Typography,
    Box,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Grid,
    Tabs,
    Tab,
    useMediaQuery,
    Stack,
    TextField,
    Paper,
    Button,
    InputAdornment
} from '@mui/material';
import {useTheme} from '@mui/material';
import {tokens} from '../../theme';
import {TitleScreen, SubTitleScreen, TabsContainer, TabStyles} from '../../SystemDesign/Styles';
import {
    useGetRolesQuery,
    useGetPostesQuery,
    useGetUsersQuery
} from '../../services/SkillsManagement';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import ListView from '../../SystemDesign/ListView';
import DateInTable from '../../SystemDesign/DateInTable';
import {formattedDate, isEmpty} from '../../Utils/Functions';
import {useDispatch} from 'react-redux';
import {INITIALIZE_ROLES, INITIALIZE_POSTES} from '../../Reducers/Actions';
import RoleChip from '../../SystemDesign/RoleChip';
import {SnackBarContext} from '../../Context/SnackBarContextProvider';
import AddEmployee from './AddEmployee';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import DeleteUserCompany from './DeleteUserCompany';
import RestoreUserCompany from './RestoreUserCompany';
import UpdateEmployee from './UpdateEmployee';
import ProfilEmployee from './ProfilEmployee';
import AssignPermissionsCompanyUser from './AssignPermissionsCompanyUser';
import AssignPosteToEmployee from './AssignPosteToEmployee';
import {AuthContext} from '../../Context/AuthContext';
import ListViewV2 from "../../SystemDesign/ListViewV2";
import useDebounce from "../../Utils/useDebounce";
import AssignSkillsToEmployees from './AssignSkillsToEmployees';

function UserCompanyList() {
    // Context and theme
    const {setInfoAlert, setOpenSnackBar} = useContext(SnackBarContext);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {user, ability} = useContext(AuthContext);
    const [isFilterOpen, setIsFilterOpen] = useState(false);


    // Add responsive breakpoints
    const isMdOrLarger = useMediaQuery((theme) => theme.breakpoints.up("md"));
    const isSmOrLarger = useMediaQuery((theme) => theme.breakpoints.up("sm"));

    // Redux dispatch
    const dispatch = useDispatch();


    // Local state
    const [columnFilters, setColumnFilters] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [isArchived, setIsArchived] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearchValue = useDebounce(searchValue, 600);
    const [queryParams, setQueryParams] = useState(() => {
        // Check localStorage for saved rowsPerPage value
        const savedRowsPerPage = localStorage.getItem(`company-users-rowsPerPage`);
        const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;

        return {
            isArchived: false,
            filters: "",
            sorting: "",
            pagination: JSON.stringify({currentPage: 1, rowsPerPage: rowsPerPage}),
            companyId: user.companyId,
            type: 'user',
        };
    });

    // Build API filters from queryParams (email, role, profile names)
    const apiFilters = React.useMemo(() => {
        const filters = {};
        const qp = queryParams || {};
        if (qp.email) {
            filters["email"] = qp.email;
        }
        if (qp.role && qp.role.roleId !== undefined) {
            filters["role.roleId"] = qp.role.roleId;
        }
        if (qp.firstName) {
            filters["profile.firstName"] = qp.firstName;
        }
        if (qp.lastName) {
            filters["profile.lastName"] = qp.lastName;
        }
        return filters;
    }, [queryParams]);

    // Merge computed apiFilters with any existing filters present in queryParams
    const userQueryArgs = React.useMemo(() => {
        const args = { ...queryParams };
        let existing = {};
        if (args.filters) {
            try {
                existing = JSON.parse(args.filters);
            } catch (e) {
                existing = {};
            }
        }
        const merged = { ...existing, ...apiFilters };
        if (Object.keys(merged).length > 0) {
            args.filters = JSON.stringify(merged);
        } else {
            delete args.filters;
        }
        return args;
    }, [queryParams, apiFilters]);

    const {
        data: employeesData,
        isLoading: isLoadingEmployees,
        error: employeesError,
        isFetching,
        refetch
    } = useGetUsersQuery(
        userQueryArgs,
        { refetchOnMountOrArgChange: true }
    );

    const { data: rolesData } = useGetRolesQuery({});

    // Extract users and roles from RTK Query data
    const employees = React.useMemo(() => {
        if (employeesData?.users?.rows) {
            return employeesData.users.rows;
        } else if (employeesData?.users && Array.isArray(employeesData.users)) {
            return employeesData.users;
        }
        return [];
    }, [employeesData]);

    const roles = React.useMemo(() => {
        if (rolesData?.roles) {
            return rolesData.roles;
        } else if (Array.isArray(rolesData)) {
            return rolesData;
        }
        return [];
    }, [rolesData]);

    // Get all postes
    const {data: postesData} = useGetPostesQuery({
        getAllWithoutPagination: true,
        pagination: JSON.stringify({currentPage: 1, rowsPerPage: 1000}),
        filters: "",
        sorting: ""
    });

    // Define available actions for each table row based on archived status and user abilities
    const actionsList = React.useMemo(() => {
        return isArchived
            ? [
                ...(ability.can('delete', 'User') ? [{component: DeleteUserCompany, key: 'delete-archived'}] : []),
                ...(ability.can('update', 'User') ? [{component: RestoreUserCompany, key: 'restore'}] : [])
            ]
            : [
                {component: ProfilEmployee, key: 'see-more'},
                ...(ability.can('update', 'User') ? [{component: UpdateEmployee, key: 'update'}] : []),
                ...(ability.can('manage', 'Userpermission') ? [{
                    component: AssignPermissionsCompanyUser,
                    key: 'assign-permissions'
                }] : []),
                ...(ability.can('manage', 'Poste') ? [{component: AssignPosteToEmployee, key: 'assign-poste'}] : []),
                ...(ability.can('delete', 'User') ? [{component: DeleteUserCompany, key: 'delete'}] : [])
            ];
    }, [isArchived, ability]);

    // Table columns configuration
    const columns = React.useMemo(() => [
        {
            id: 'username',
            accessorKey: 'username',
            header: 'Username',
            filterFn: 'contains',
            type: 'text',
            enableColumnFilter: false,

        },
        ...(isSmOrLarger ? [{
            id: 'profile.firstName',
            accessorKey: 'profile.firstName',
            header: 'First name',
            enableColumnFilter: false,
        },
            {
                id: 'profile.lastName',
                accessorKey: 'profile.lastName',
                header: 'Last name',
                enableColumnFilter: false,
            }] : []),
        ...(isMdOrLarger ? [{
            id: 'email',
            accessorKey: 'email',
            header: 'Email',
            enableColumnFilter: false,
        }] : []),
        // Only show role column if user has ability to read Role
        ...(ability.can('read', 'Role') ? [{
            id: 'roles.type',
            accessorKey: 'role.type',
            cell: (info) => <RoleChip role={info.getValue()}/>,
            header: 'Role',
            filterFn: 'equals',
            multiSelect: true,
            uniqueValues: roles ? [...roles.map(role => role.type)] : [],
        }] : []),
        ...(isMdOrLarger ? [{
            id: 'position',
            accessorKey: 'Poste',
            header: 'Position',
            enableColumnFilter: false,
            cell: (info) => {
                const poste = info.getValue();
                if (!poste || !poste.name) return '-';
                const skillsCount = poste.skillRequirements?.length || 0;
                return `${poste.name} (${skillsCount} skill${skillsCount !== 1 ? 's' : ''})`;
            },
        }] : []),
        ...(isMdOrLarger ? [{
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: 'Created at',
            enableColumnFilter: false,
            cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))}/>,
            filterFn: 'between',
            type: 'date',
        }] : []),
        // Colonne updatedAt avec sa propre condition responsive
        ...(isMdOrLarger ? [{
            id: 'updatedAt',
            accessorKey: 'updatedAt',
            header: 'Updated at',
            enableColumnFilter: false,
            cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))}/>,
            filterFn: 'between',
            type: 'date',
        }] : []),
        ...(actionsList.length > 0 ? [{
            id: 'actions',
            header: '',
            size: 100,
            enableColumnFilter: false,
            cell: ({row}) => (
                <ListViewMenuActions key={`ListViewMenuActions-${row.id}-${isArchived}`}>
                    {actionsList.map(({component: Component, key}) => (
                        <Component key={`${key}-${row.id}-${isArchived}`} row={row} actionKey={key}/>
                    ))}
                </ListViewMenuActions>
            ),
        }] : [])
    ], [isArchived, isMdOrLarger, isSmOrLarger, ability, roles]);

    // Handle tab changes - just set isArchived based on tab
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        const newArchivedStatus = newValue === 1;
        setIsArchived(newArchivedStatus);

        // Get current rowsPerPage preference from localStorage or use default
        const savedRowsPerPage = localStorage.getItem(`company-users-rowsPerPage`);
        const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;

        // Reset filters, sorting, and pagination when changing tabs
        setColumnFilters([]);
        setSearchValue('')
        setQueryParams({
            isArchived: newArchivedStatus,  // This is the key parameter - true for archived tab, false for active tab
            filters: "",
            sorting: "",
            pagination: JSON.stringify({currentPage: 1, rowsPerPage: rowsPerPage}),
            companyId: user.companyId,
            type: 'user',
        });
        refetch();
    };

    // Also simplify applyFilters to just maintain the current isArchived value
    const applyFilters = (updatedFilters = columnFilters, paginationInfo = null, sortingInfo = null, searchValue = null) => {
        const filtersArray = updatedFilters || [];

        const formattedFilters = filtersArray.reduce((acc, filter) => {
            if (typeof filter === 'object' && !Array.isArray(filter)) {
                const [key, values] = Object.entries(filter)[0];

                // Skip empty values
                if (values === "" || (Array.isArray(values) && values.length === 0)) {
                    return acc;
                }

                // Handle date range filters (between)
                if (Array.isArray(values) && columns.find(col => col.id === key && col.filterFn === 'between')) {
                    // Only include date filters where at least one value is non-empty
                    const nonEmptyValues = values.filter(val => val !== "" && val !== null && val !== undefined);

                    if (nonEmptyValues.length === 0) {
                        return acc; // Skip this filter if all values are empty
                    }

                    // For date ranges, ensure we have exactly two values (start and end)
                    // If one is missing, we'll keep only the one that exists
                    acc[key] = nonEmptyValues;
                }
                // Regular text filter (contains)
                else if (columns.find(col => col.id === key && col.filterFn === 'contains')) {
                    // Pour les filtres de type texte, on veut un tableau avec une seule valeur
                    acc[key] = [String(values)];
                }
                // Other filter types (equals, multiselect)
                else {
                    // Pour les autres types de filtres
                    acc[key] = Array.isArray(values)
                        ? values.filter(val => val !== "" && val !== null && val !== undefined).map(val => String(val))
                        : [String(values)];
                }
            }
            return acc;
        }, {});

        // Create query params object
        const newQueryParams = {
            isArchived: isArchived,
            pagination: JSON.stringify({
                currentPage: paginationInfo?.currentPage || 1,
                rowsPerPage: paginationInfo?.rowsPerPage || 10
            }),
            companyId: user.companyId,
            type: 'user',
        };

        // Only add non-empty parameters
        if (!isEmpty(formattedFilters)) {
            newQueryParams.filters = JSON.stringify(formattedFilters);
        }

        if (sortingInfo) {
            newQueryParams.sorting = JSON.stringify(sortingInfo);
        }

        if (searchValue) {
            newQueryParams.search = searchValue;
        }

        setQueryParams(newQueryParams);
    };

    const handleSearch = (value) => {
        setSearchValue(value);
    }

    // Banner filters state
    const [emailFilter, setEmailFilter] = useState('');
    const [firstNameFilter, setFirstNameFilter] = useState('');
    const [lastNameFilter, setLastNameFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState('');

    const updatePaginationFirstPage = (prev) => {
        let rowsPerPage = 10;
        try {
            const parsed = JSON.parse(prev.pagination || '{}');
            if (parsed && parsed.rowsPerPage) rowsPerPage = parsed.rowsPerPage;
        } catch {}
        return JSON.stringify({ currentPage: 1, rowsPerPage });
    };

    const applyBannerFilters = (nextEmail, nextFirstName, nextLastName, nextRoleId) => {
        setQueryParams(prev => {
            const updated = { ...prev };
            if (nextEmail && nextEmail.trim() !== '') {
                updated.email = nextEmail.trim();
            } else {
                delete updated.email;
            }
            if (nextFirstName && nextFirstName.trim() !== '') {
                updated.firstName = nextFirstName.trim();
            } else {
                delete updated.firstName;
            }
            if (nextLastName && nextLastName.trim() !== '') {
                updated.lastName = nextLastName.trim();
            } else {
                delete updated.lastName;
            }
            if (nextRoleId && String(nextRoleId).length > 0) {
                updated.role = { roleId: Number(nextRoleId) };
            } else {
                delete updated.role;
            }
            updated.pagination = updatePaginationFirstPage(prev);
            return updated;
        });
    };

    const handleEmailFilterChange = (e) => {
        const val = e.target.value;
        setEmailFilter(val);
    };

    const handleFirstNameFilterChange = (e) => {
        const val = e.target.value;
        setFirstNameFilter(val);
    };

    const handleLastNameFilterChange = (e) => {
        const val = e.target.value;
        setLastNameFilter(val);
    };

    const handleRoleFilterChange = (e) => {
        const val = e.target.value;
        setRoleFilter(val);
    };

    const clearBannerFilters = () => {
        setEmailFilter('');
        setFirstNameFilter('');
        setLastNameFilter('');
        setRoleFilter('');
        applyBannerFilters('', '', '', '');
    };

    // Debounced filter changes to reduce number of requests
    const debouncedEmail = useDebounce(emailFilter, 500);
    const debouncedFirstName = useDebounce(firstNameFilter, 500);
    const debouncedLastName = useDebounce(lastNameFilter, 500);
    // Role is a select; we can still debounce lightly to coalesce rapid changes
    const debouncedRole = useDebounce(roleFilter, 150);

    useEffect(() => {
        // Single place to propagate debounced filters
        const debouncedFilterChange = () => {
            applyBannerFilters(debouncedEmail, debouncedFirstName, debouncedLastName, debouncedRole);
        };
        debouncedFilterChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedEmail, debouncedFirstName, debouncedLastName, debouncedRole]);

    // Initialize roles in Redux
    useEffect(() => {
        if (rolesData?.roles) {
            dispatch({type: INITIALIZE_ROLES, payload: rolesData.roles});
        } else if (Array.isArray(rolesData)) {
            dispatch({type: INITIALIZE_ROLES, payload: rolesData});
        }
    }, [rolesData, dispatch]);

    // Initialize postes in Redux
    useEffect(() => {
        if (postesData?.postes) {
            dispatch({type: INITIALIZE_POSTES, payload: postesData.postes});
        }
    }, [postesData?.postes, dispatch]);

    // Error handling
    useEffect(() => {
        if (employeesError) {
            setInfoAlert({
                severity: 'error',
                message: 'Une erreur est survenue lors de la récupération des employés'
            });
            setOpenSnackBar(true);
        }
    }, [employeesError]);


    return (
        <div>
            <Typography sx={TitleScreen(colors)}>User Management</Typography>
            <Typography sx={SubTitleScreen(colors)}>List of users</Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%', justifyContent: 'flex-end' }}>
                <AssignSkillsToEmployees />
                {ability.can('create', 'User') && <AddEmployee/>}
            </Stack>
            <Box sx={TabsContainer}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{minHeight: '50px'}}
                >
                    <Tab
                        icon={<PersonOutlineRoundedIcon fontSize="small"/>}
                        iconPosition="start"
                        label="active"
                        sx={TabStyles}
                    />
                    <Tab
                        icon={<PersonOffRoundedIcon fontSize="small"/>}
                        iconPosition="start"
                        label="archived"
                        sx={TabStyles}
                    />
                </Tabs>
            </Box>

            <LoaderSpinner loading={isLoadingEmployees}/>
            {/* Banner filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                            label="Search by email"
                            size="small"
                            value={emailFilter}
                            onChange={handleEmailFilterChange}
                            placeholder="name@domain.com"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">@</InputAdornment>
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} lg={2}>
                        <TextField
                            label="First name"
                            size="small"
                            value={firstNameFilter}
                            onChange={handleFirstNameFilterChange}
                            placeholder="First name"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} lg={2}>
                        <TextField
                            label="Last name"
                            size="small"
                            value={lastNameFilter}
                            onChange={handleLastNameFilterChange}
                            placeholder="Last name"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={8}>
                        <FormControl size="small" fullWidth sx={{ minWidth: { md: 220, lg: 220 } }}>
                            <InputLabel id="role-filter-label">Role</InputLabel>
                            <Select
                                labelId="role-filter-label"
                                label="Role"
                                value={roleFilter}
                                onChange={handleRoleFilterChange}
                            >
                                <MenuItem value=""><em>All roles</em></MenuItem>
                                {(rolesData?.roles || rolesData)?.map((r) => (
                                    <MenuItem key={r.id || r.roleId} value={r.id || r.roleId}>{r.type || r.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md="auto" lg="auto" sx={{ ml: { md: 'auto' } }}>
                        <Button onClick={clearBannerFilters} variant="text" fullWidth={false}>Clear</Button>
                    </Grid>
                </Grid>
            </Paper>
            <ListViewV2
                key="fefee fefe"
                dispatchData={employees}
                columns={columns}
                tableName="company-users"
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                applyFilters={applyFilters}
                pagination={employeesData?.pagination}
                setQueryParams={setQueryParams}
                queryParams={queryParams}
                debouncedSearchValue={debouncedSearchValue}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}

            />


        </div>
    );
}

export default UserCompanyList;